import React, { useState } from "react";
import {
  AppBar,
  Typography,
  Button,
  Container,
  IconButton,
  useMediaQuery,
  useTheme,
  Box,
  styled,
  List,
  ListItem,
  ListItemText,
  Divider,
  Backdrop,
  Slide,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Link, useNavigate } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import logo from "../assets/mainLogo.jpg";

const NavButton = styled(Button)(({ theme }) => ({
  color: "#333",
  fontWeight: 600,
  fontSize: "0.83rem",
  textTransform: "uppercase",
  letterSpacing: 1,
  padding: "8px 12px",
  "&:hover": {
    borderRadius: "50px",
    backgroundColor: "#AD1C86",
    color: "#fff",
  },
  [theme.breakpoints.down("lg")]: {
    fontSize: "0.75rem",
    padding: "6px 8px",
  },
}));

const SchoolLogo = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  [theme.breakpoints.down("sm")]: {
    gap: "8px",
  },
}));

const DrawerHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(2),
  justifyContent: "space-between",
  borderBottom: "1px solid #eee",
}));

const Navbar = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [subMenuOpen, setSubMenuOpen] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);

  const navItems = [
    { name: "Home", path: "/" },
    {
      name: "About Us",
      path: "/about",
      subItems: [
        { name: "About School", path: "/about-school" },
        { name: "Vision", path: "/vision" },
        { name: "Mission", path: "/mission" },
        { name: "Principal Desk", path: "/principal-desk" },
        { name: "Director Message", path: "/director-message" },
      ],
    },

     {
      name: "Exams",
      path: "/online-exams",
      subItems: [
        // {name : "Exam Registration", path: "/register-exam"},
        { name: "Admit Card", path: "/admit-card" },
        { name: "Online Exams", path: "/online-exams" },
        { name: "Reports", path: "/reports" },
        { name: "Top Reports", path: "/top-reports" },
      ],
    },

    {
      name: "Special Programs",
      path: "special-programs",
      subItems: [
        { name: "Navodaya", path: "/navodaya" },
        { name: "RIMC", path: "/RIMC" },
        { name: "Sainik", path: "/sainik" },
        { name: "Military", path: "/military" },
      ],
    },
    {
      name: "Admission",
      path: "/admission",
      subItems: [
        { name: "Admission Process", path: "/admission-process" },
        { name: "Admission Form", path: "/admission-form" },
        // { name: "Online Exams", path: "/online-exams" },
        { name: "Online Fees", path: "/online-fees" },
      ],
    },
    {
      name: "Student's Corner",
      path: "/students-corner",
      subItems: [
        { name: "Activities", path: "/activities" },
        { name: "Sports", path: "/sports" },
        { name: "Environment", path: "/environment" },
        { name: "Counselling", path: "/counselling" },
      ],
    },
    {
      name: "Academics",
      path: "/academic",
      subItems: [
        { name: "Academic Calendar", path: "/academic-calendar" },
        { name: "Exam Time Table", path: "/academic-exam-time-table" },
      ],
    },
    {
      name: "Boarders",
      path: "/boarders",
      subItems: [
        { name: "Library Facility", path: "/library-facility" },
        { name: "Hostel Facility", path: "/hostel-facility" },
        { name: "Mess Facility", path: "/mess-facility" },
      ],
    },
    { name: "News & Events", path: "/news-events" },
    { name: "Gallery", path: "/gallery" },
    { name: "Contact", path: "/contact" },
  ];

  const handleMenuOpen = (menuName) => {
    if (!isMobile) {
      setActiveMenu(menuName);
    }
  };

  const handleMenuClose = () => {
    setActiveMenu(null);
  };

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setMobileOpen(open);
    if (!open) {
      setSubMenuOpen(null);
    }
  };

  const handleSubMenuToggle = (itemName) => {
    setSubMenuOpen(subMenuOpen === itemName ? null : itemName);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileOpen(false);
    setSubMenuOpen(null);
    setActiveMenu(null);
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        boxShadow: 2,
        // position: "fixed",
        position: "sticky",
        top: 0,
        left: 0,
        width: "100%",
        zIndex: 1000,
        backgroundColor: "#fff",
      }}
    >
      {/* Main Header */}
      <AppBar
        position="static"
        sx={{
          backgroundColor: "white",
          color: "black",
          boxShadow: "none",
          borderBottom: "1px solid #eee",
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              py: 2,
            }}
          >
            {/* School Name and Contact Info */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Link to="/" style={{ textDecoration: "none" }}>
                <SchoolLogo sx={{ backgroundColor: "#065A79", pr: 1 }}>
                  <img
                    src={logo}
                    style={{
                      width: "50px",
                      height: "50px",
                      borderLeft: "5px solid #065A79",
                    }}
                  />
                  <Typography
                    variant="h6"
                    noWrap
                    component="div"
                    sx={{
                      fontFamily: "Georgia, serif",
                      fontWeight: 700,
                      fontSize: "1.8rem",
                      color: "#fff",
                      textTransform: "uppercase",
                      [theme.breakpoints.down("lg")]: {
                        fontSize: "1.5rem",
                      },
                      [theme.breakpoints.down("sm")]: {
                        fontSize: "1.2rem",
                      },
                    }}
                  >
                    S.M.S.SCHOOL
                  </Typography>
                </SchoolLogo>
              </Link>

              {/* Desktop Contact Info - Hidden on mobile */}
              <Box
                sx={{
                  display: { xs: "none", md: "flex" },
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, ml: 3 }}
                >
                  <LocationOnIcon fontSize="small" sx={{ color: "#AD1C86" }} />
                  <Typography
                    component="a"
                    href="https://www.google.com/maps/place/Shree+Mangalam+School+%2Facademy+gudha/@27.8665696,75.516023,15z/data=!4m6!3m5!1s0x396ccd605d059b21:0xfeb4cb05a7d17e91!8m2!3d27.8597019!4d75.5100027!16s%2Fg%2F11c3ss2s1_?entry=ttu&g_ep=EgoyMDI1MTIwOS4wIKXMDSoKLDEwMDc5MjA2OUgBUAM%3D"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                    sx={{
                      color: "#000000",
                      fontFamily: "Montserrat, sans-serif",
                      fontOpticalSizing: "auto",
                      fontStyle: "normal",
                      //  fontSize: "0.875rem"
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {/* Jaipur Gudha State Highway, District Jhunjhunu, Raj. pin 333022 */}
                    Jaipur-Gudha State Highway (Just on by pass) In Gudha Gorji
                    Raj.
                  </Typography>
                </Box>

                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, ml: 2 }}
                >
                  <PhoneIcon fontSize="small" sx={{ color: "#AD1C86" }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#555",
                      fontFamily: "Montserrat, sans-serif",
                      fontOpticalSizing: "auto",
                      fontStyle: "normal",
                    }}
                  >
                    +919928263305, +919413645511,
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: { lg: "flex", md: "none" },
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <EmailIcon fontSize="small" sx={{ color: "#AD1C86" }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#555",
                      fontFamily: "Montserrat, sans-serif",
                      fontOpticalSizing: "auto",
                      fontStyle: "normal",
                    }}
                  >
                    shreemangalamacademy11@gmail.com
                  </Typography>
                </Box>
              </Box>

              {/* Mobile Menu Button */}
              <Box sx={{ display: { xs: "flex", lg: "none" } }}>
                <Button
                  variant="text"
                  onClick={toggleDrawer(true)}
                  startIcon={
                    <MenuIcon
                      sx={{
                        transition:
                          "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    />
                  }
                  sx={{
                    backgroundColor: "#AD1C86",
                    color: "#fff",
                    textTransform: "none",
                    fontWeight: 600,
                    padding: "8px 12px 10px 10px",
                    letterSpacing: "1.5px",
                    lineHeight: "3px",
                    borderRadius: "50px",
                    transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      borderRadius: "50px",
                      backgroundColor: "#065A79",
                    },
                  }}
                >
                  Menu
                </Button>
              </Box>
            </Box>

            {/* Desktop Navigation - Hidden on mobile */}
            {!isMobile && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  borderTop: "1px solid #eee",
                  pt: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: "2px",
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  {navItems.map((item) => (
                    <Box
                      key={item.name}
                      onMouseEnter={() => handleMenuOpen(item.name)}
                      onMouseLeave={handleMenuClose}
                      sx={{ position: "relative", borderRadius: "50px" }}
                    >
                      <NavButton
                        onClick={() => {
                          if (!item.subItems) {
                            handleNavigation(item.path);
                          }
                        }}
                        endIcon={item.subItems ? <ExpandMoreIcon /> : null}
                      >
                        {item.name}
                      </NavButton>

                      {item.subItems && activeMenu === item.name && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            zIndex: 1,
                            backgroundColor: "white",
                            boxShadow: 2,
                            borderRadius: 2,
                            minWidth: "200px",
                            transition: "all 0.3s ease",
                            opacity: 1,
                            visibility: "visible",
                            transform: "translateY(0)",
                            borderBottom: "3px solid #065A79",
                          }}
                        >
                          {item.subItems.map((subItem) => (
                            <NavButton
                              key={subItem.name}
                              onClick={() => handleNavigation(subItem.path)}
                              sx={{
                                display: "block",
                                width: "100%",
                                textAlign: "left",
                                justifyContent: "flex-start",
                                cursor: "pointer",
                                mt: 1,
                                mb: 1,
                                "&:hover": {
                                  borderRadius: "50px",
                                  backgroundColor: "#AD1C86",
                                  color: "#fff",
                                },
                              }}
                            >
                              {subItem.name}
                            </NavButton>
                          ))}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Container>
      </AppBar>
      {/* Mobile Drawer */}
      <React.Fragment>
        <Backdrop
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: "rgba(0,0,0,0.5)",
            transition: "opacity 0.5s ease",
            opacity: mobileOpen ? 1 : 0,
            visibility: mobileOpen ? "visible" : "hidden",
          }}
          open={mobileOpen}
          onClick={toggleDrawer(false)}
        />
        <Slide
          direction="left"
          in={mobileOpen}
          timeout={300}
          mountOnEnter
          unmountOnExit
        >
          <Box
            sx={{
              position: "fixed",
              right: 0,
              top: 0,
              height: "100vh",
              width: "80%",
              maxWidth: "300px",
              bgcolor: "background.paper",
              boxShadow: 24,
              zIndex: (theme) => theme.zIndex.drawer + 2,
            }}
          >
            {/* Drawer Header with Logo and Close Button */}
            <DrawerHeader>
              <Link to="/" style={{ textDecoration: "none" }}>
                <SchoolLogo>
                  <img
                    src={logo}
                    style={{
                      width: "50px",
                      height: "50px",
                      borderLeft: "5px solid #065A79",
                    }}
                  />
                  <Typography
                    variant="h6"
                    component="div"
                    sx={{
                      fontFamily: "Georgia, serif",
                      fontWeight: 700,
                      fontSize: "1.1rem",
                      color: "#1a5f9c",
                      textTransform: "uppercase",
                    }}
                  >
                    S.M.S.SCHOOL
                  </Typography>
                </SchoolLogo>
              </Link>
              <IconButton
                onClick={toggleDrawer(false)}
                sx={{
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  color: "#065A79",
                  "&:hover": {
                    transform: "rotate(90deg) scale(1.1)",
                    backgroundColor: "rgb(173, 28, 134)",
                    color: "#fff",
                  },
                }}
              >
                <CloseIcon
                  sx={{
                    color: "#065A79",
                    "&:hover": {
                      color: "#fff",
                    },
                  }}
                />
              </IconButton>
            </DrawerHeader>
            <Divider />
            {/* Navigation Items */}
            <List>
              {navItems.map((item) => (
                <React.Fragment key={item.name}>
                  {item.subItems ? (
                    <React.Fragment>
                      <ListItem
                        button
                        onClick={() => handleSubMenuToggle(item.name)}
                        sx={{
                          cursor: "pointer",
                          letterSpacing: 1.5,
                          "&:hover": {
                            backgroundColor: "#AD1C86",
                            color: "#fff",
                            borderRadius: "50px",
                          },
                        }}
                      >
                        <ListItemText primary={item.name} />
                        {subMenuOpen === item.name ? (
                          <ExpandMoreIcon />
                        ) : (
                          <ChevronRightIcon />
                        )}
                      </ListItem>
                      {subMenuOpen === item.name && (
                        <>
                          <List
                            sx={{
                              pl: 3,
                              top: "100%",
                              left: 0,
                              zIndex: 1,
                              backgroundColor: "white",
                              boxShadow: 2,
                              borderRadius: 1,
                              minWidth: "200px",
                              transition: "all 0.3s ease",
                              opacity: 1,
                              visibility: "visible",
                              transform: "translateY(0)",
                              borderBottom: "3px solid #065A79",
                              pr: 3,
                            }}
                          >
                            {item.subItems.map((subItem) => (
                              <ListItem
                                key={subItem.name}
                                button
                                onClick={() => handleNavigation(subItem.path)}
                                sx={{
                                  justifyContent: "flex-start",
                                  width: "100%",
                                  borderRadius: 0,
                                  letterSpacing: 1.5,
                                  "&:hover": {
                                    borderRadius: "50px",
                                    backgroundColor: "#AD1C86",
                                    color: "#fff",
                                  },
                                }}
                              >
                                <ListItemText
                                  primary={subItem.name}
                                  sx={{
                                    cursor: "pointer",
                                    letterSpacing: 1.5,
                                    "&:hover": {
                                      backgroundColor: "#AD1C86",
                                      color: "#fff",
                                    },
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </>
                      )}
                    </React.Fragment>
                  ) : (
                    <ListItem
                      button
                      onClick={() => handleNavigation(item.path)}
                      sx={{
                        "&:hover": {
                          backgroundColor: "#AD1C86",
                          color: "#fff",
                          borderRadius: "50px",
                        },
                      }}
                    >
                      <ListItemText
                        primary={item.name}
                        sx={{
                          letterSpacing: 1.5,
                          cursor: "pointer",
                        }}
                      />
                    </ListItem>
                  )}
                </React.Fragment>
              ))}
            </List>
          </Box>
        </Slide>
      </React.Fragment>
    </Box>
  );
};

export default Navbar;
